import { TerritoryType, type TerritoryTypeValue } from '@dreamgame/shared';
import { tv } from 'tailwind-variants';
import { playerId } from '../../state/entities';
import { selectedTerritory } from '../../state/territories';

// ============================================================================
// Types & Constants
// ============================================================================

const getTerritoryTypeName = (type: TerritoryTypeValue): string => {
  switch (type) {
    case TerritoryType.HOME:
      return 'Home Territory';
    case TerritoryType.OUTPOST:
      return 'Outpost';
    default:
      return 'Territory';
  }
};

const getTerritoryDescription = (type: TerritoryTypeValue): string => {
  switch (type) {
    case TerritoryType.HOME:
      return 'Invulnerable safe zone';
    case TerritoryType.OUTPOST:
      return 'Can be raided by enemies';
    default:
      return 'Claimed land';
  }
};

// ============================================================================
// Styles
// ============================================================================

// Custom gradient for borders based on territory type
const HOME_GRADIENT = 'bg-gradient-to-b from-[#e9f0fb] via-[#3f6bbf] to-[#2858b3]';
const OUTPOST_GRADIENT = 'bg-gradient-to-b from-[#fbe9e9] via-[#bf533f] to-[#b33828]';

const territoryInfoPanel = tv({
  slots: {
    root: ['fixed top-4 right-4 z-[1000]', 'w-72', 'animate-slide-left', 'font-sans'],
    // The main container with the metallic border effect
    borderFrame: ['p-[2px]', 'rounded-sm', 'shadow-2xl'],
    // The dark inner content background
    contentFrame: [
      'bg-[#0b1a1a]',
      'bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-[#162c2c] to-[#0b1a1a]',
      'p-1',
      'rounded-sm',
      'relative',
      'overflow-hidden',
    ],
    // Inner decorative line
    innerBorder: ['border border-[#335050]/50', 'p-4', 'relative'],

    // Header
    header: 'flex flex-col items-center mb-4 relative z-10',
    titleDecoration: ['w-full h-[2px] mb-2', 'opacity-80'],
    name: [
      'text-2xl',
      'font-[family-name:var(--font-fantasy)]',
      'text-transparent bg-clip-text',
      'drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)]',
      'tracking-widest',
      'uppercase',
      'text-center',
    ],
    typeBadge: [
      'mt-1',
      'text-[10px] uppercase tracking-[0.2em]',
      'text-[#557788]',
      'flex items-center gap-2',
      'before:content-[""] before:w-3 before:h-[1px] before:bg-[#557788]',
      'after:content-[""] after:w-3 after:h-[1px] after:bg-[#557788]',
    ],

    // Content
    section: 'mb-4 relative',
    sectionHeader: [
      'flex items-center gap-3 mb-3',
      'text-xs uppercase tracking-[0.15em]',
      'font-bold',
      'after:content-[""] after:flex-1 after:h-[1px] after:to-transparent',
    ],

    statsGrid: 'grid grid-cols-2 gap-3',
    statBox: [
      'bg-[#060f0f]/60',
      'border border-[#1f3d3d]',
      'p-2',
      'flex flex-col items-center justify-center',
      'rounded-sm',
    ],
    statLabel: 'text-[9px] uppercase tracking-wider text-[#557788] mb-0.5',
    statValue: 'text-[#c4dce8] font-mono text-sm',

    // Owner section
    ownerSection: [
      'flex items-center gap-3',
      'bg-[#060f0f]/40',
      'border border-[#1f3d3d]',
      'p-3',
      'rounded-sm',
    ],
    ownerIcon: ['w-10 h-10', 'rounded-full', 'flex items-center justify-center', 'text-lg'],
    ownerInfo: 'flex flex-col',
    ownerLabel: 'text-[9px] uppercase tracking-wider text-[#557788]',
    ownerName: 'text-[#c4dce8] font-medium',

    // Footer
    footer: [
      'mt-2 pt-3',
      'border-t border-[#1f3d3d]',
      'flex justify-between items-center',
      'text-[10px] text-[#3d5a5a] font-mono',
    ],

    // Corner flourishes
    cornerTL: 'absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 rounded-tl-sm opacity-60',
    cornerTR: 'absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 rounded-tr-sm opacity-60',
    cornerBL: 'absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 rounded-bl-sm opacity-60',
    cornerBR: 'absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 rounded-br-sm opacity-60',
  },
});

// ============================================================================
// Component
// ============================================================================

export const TerritoryInfoPanel = () => {
  const territory = selectedTerritory.value;

  if (!territory) {
    return null;
  }

  const typeName = getTerritoryTypeName(territory.territoryType);
  const description = getTerritoryDescription(territory.territoryType);
  const isHome = territory.territoryType === TerritoryType.HOME;
  const isOwned = territory.ownerId === playerId.value;
  const areaSize = (territory.radius * 2 + 1) * (territory.radius * 2 + 1);

  const styles = territoryInfoPanel();

  // Colors based on territory type
  const accentColor = isHome ? '#5588ee' : '#ee5533';
  const gradient = isHome ? HOME_GRADIENT : OUTPOST_GRADIENT;
  const titleGradient = isHome
    ? 'bg-gradient-to-b from-[#e7f0ff] to-[#5588cc]'
    : 'bg-gradient-to-b from-[#ffe7e7] to-[#cc5533]';
  const decorationGradient = isHome
    ? 'bg-[linear-gradient(90deg,transparent,#5588ee,transparent)]'
    : 'bg-[linear-gradient(90deg,transparent,#ee5533,transparent)]';
  const sectionHeaderColor = isHome ? 'text-[#5588ee]' : 'text-[#ee5533]';
  const sectionLineGradient = isHome ? 'after:from-[#5588ee]/50' : 'after:from-[#ee5533]/50';
  const cornerColor = isHome ? 'border-[#5588ee]' : 'border-[#ee5533]';

  return (
    <div class={styles.root()}>
      {/* Outer Metallic Frame */}
      <div class={`${styles.borderFrame()} ${gradient}`}>
        {/* Inner Content Background */}
        <div class={styles.contentFrame()}>
          {/* Inner Decorative Border */}
          <div class={styles.innerBorder()}>
            {/* Corner Flourishes */}
            <div class={`${styles.cornerTL()} ${cornerColor}`} />
            <div class={`${styles.cornerTR()} ${cornerColor}`} />
            <div class={`${styles.cornerBL()} ${cornerColor}`} />
            <div class={`${styles.cornerBR()} ${cornerColor}`} />

            {/* Header Area */}
            <div class={styles.header()}>
              <div class={`${styles.titleDecoration()} ${decorationGradient}`} />
              <h2 class={`${styles.name()} ${titleGradient}`}>{typeName}</h2>
              <div class={styles.typeBadge()}>{description}</div>
            </div>

            {/* Owner Section */}
            <div class={styles.section()}>
              <div class={`${styles.sectionHeader()} ${sectionHeaderColor} ${sectionLineGradient}`}>
                Owner
              </div>
              <div class={styles.ownerSection()}>
                <div
                  class={styles.ownerIcon()}
                  style={{
                    backgroundColor: `${accentColor}33`,
                    border: `2px solid ${accentColor}`,
                  }}
                >
                  {isOwned ? '👑' : '⚔'}
                </div>
                <div class={styles.ownerInfo()}>
                  <span class={styles.ownerLabel()}>
                    {isOwned ? 'Your territory' : 'Enemy territory'}
                  </span>
                  <span class={styles.ownerName()}>
                    {territory.ownerName ?? `Player #${territory.ownerId}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Territory Stats */}
            <div class={styles.section()}>
              <div class={`${styles.sectionHeader()} ${sectionHeaderColor} ${sectionLineGradient}`}>
                Territory Info
              </div>
              <div class={styles.statsGrid()}>
                <div class={styles.statBox()}>
                  <span class={styles.statLabel()}>Protection</span>
                  <span class={styles.statValue()}>{isHome ? 'Full' : 'Partial'}</span>
                </div>
                <div class={styles.statBox()}>
                  <span class={styles.statLabel()}>Area</span>
                  <span class={styles.statValue()}>{areaSize} tiles</span>
                </div>
                <div class={styles.statBox()}>
                  <span class={styles.statLabel()}>Radius</span>
                  <span class={styles.statValue()}>{territory.radius} tiles</span>
                </div>
                <div class={styles.statBox()}>
                  <span class={styles.statLabel()}>Raidable</span>
                  <span class={styles.statValue()}>{isHome ? 'No' : 'Yes'}</span>
                </div>
              </div>
            </div>

            {/* Footer Information */}
            <div class={styles.footer()}>
              <span>
                CENTER: {territory.centerX}, {territory.centerY}
              </span>
              <span style={{ color: accentColor }}>{isHome ? 'SAFE ZONE' : 'RAID TARGET'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
